// Single source of truth for prerendered route paths.
//
// Build wiring:
//   - scripts/prerender.mjs imports the SSR bundle and asserts these paths
//     match the generated route metadata.
//   - scripts/check-prerender.mjs imports generated help routes from the SSR
//     bundle and verifies the dist output/sitemap after prerender.
//   - App.tsx still owns the matching browser routes.
//
// IMPORTANT: the core list MUST stay in sync with the CORE_ROUTES map in
// scripts/prerender.mjs (drift asserted at build time) and the public routes in
// App.tsx. Help article routes use lightweight slugs here so runtime hydration
// checks do not pull article bodies into the boot bundle; build-time drift
// checks still assert these paths against the prerender metadata generated from
// the article source.
export const CORE_PRERENDER_PATHS = [
  '/',
  '/app/privacy',
  '/app/terms',
  '/zh/',
  '/zh/app/privacy',
  '/zh/app/terms',
] as const;

export const PUBLIC_HELP_ARTICLE_IDS = [
  'credits-explained',
  'credits-costs',
  'image-quickstart',
  'faq',
  'contact-support',
] as const;

const HELP_PRERENDER_PATHS = PUBLIC_HELP_ARTICLE_IDS.flatMap((articleId) => [
  `/help/${articleId}` as const,
  `/zh/help/${articleId}` as const,
]);

export const PRERENDER_PATHS = [
  ...CORE_PRERENDER_PATHS,
  '/help',
  '/zh/help',
  ...HELP_PRERENDER_PATHS,
] as const;

export type PrerenderPath = typeof PRERENDER_PATHS[number];

// Pre-normalize PRERENDER_PATHS to drop trailing slashes for lookup. Keeping
// the original PRERENDER_PATHS exported value with /zh/ retained because
// prerender.mjs ROUTES uses the same form, and drift assertions compare
// against that. The lookup-side normalization makes isPrerenderPath('/zh/')
// AND isPrerenderPath('/zh') both return true — fixing the asymmetry that
// stored '/zh/' in the list but stripped trailing slash on input.
const NORMALIZED_PRERENDER_PATHS: ReadonlySet<string> = new Set(
  PRERENDER_PATHS.map((p) => p.replace(/\/+$/, '') || '/'),
);

export function isPrerenderPath(path: string): boolean {
  const normalized = path.replace(/\/+$/, '') || '/';
  return NORMALIZED_PRERENDER_PATHS.has(normalized);
}
